<template>
  <section class="page-agence">
    <!-- HEADER -->
    <section class="hero-small">
      <div>
        <h1>
          Votre agence Maisons Alysia <br/>{{ agency ? agency.name : '' }}
        </h1>
      </div>
    </section>
    <div v-if="agency">
      <div class="team">
        <div class="grid-container">
          <h2>L'équipe</h2>
          <p>
            Chez {{ agency ? agency.name : '' }}, nous sommes fiers de vous accompagner dans la réalisation de votre projet de construction de maison individuelle. Notre équipe, composée de professionnels passionnés et expérimentés, est à votre disposition pour vous offrir un service personnalisé et de qualité.
          </p>
          <div class="members">
            <div v-for="member in members" :key="member.memberId">
              <img v-if="member.photo" :key="member.memberId" :src="member.photo" />
              <img v-else src="@/assets/img/avatar-profile.svg" alt="avatar"/>
              <h3>{{ member.firstname }} {{ member.lastname }}</h3>
              <h4>{{ jobTitles[member.jobTitle][member.gender.toLowerCase()].title }}</h4>
              <p>
                {{ jobTitles[member.jobTitle][member.gender.toLowerCase()].description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="agency" class="agency-contact-container">
      <div class="grid-container">
        <h2 class="text-center">Les infos pratiques</h2>
        <p>Notre équipe est à votre écoute pour concrétiser vos envies.</p>
        <div>
          <div class="agency-map-container">
            <AgencyMap :address="agency.address"/>
          </div>
          <div class="agency-contact">
            <h3>{{ agency.name }}</h3>
            <img class="outside" :src="outsideMedias[1] ? outsideMedias[1].url : require('@/assets/img/pages/alysia-exterieur.png')" alt="Maisons alysia" />
            <p>
              {{ agency.address.addressLine1 }}<br/>
              {{ agency.address.addressLine2 }}<br v-if="agency.address.addressLine2" />
              {{ agency.address.postalCode }} {{ agency.address.city }}<br/>
            </p>
            <a class="phone" :href="`tel:${agency.phone}`" v-if="showPhone">
              {{ agency.phone }}
            </a>
            <app-button class="phone btn-agency-see-phone" v-else @click="showPhone = true" size="small">Voir le numéro</app-button>
            <pre>{{ agency.openingHours }}</pre>
            <app-button hasArrow @click="scrollToContactForm">Nous écrire</app-button>
            <router-link :to="`/agences/${department}`">Voir toutes les agences de <span class="department-name">{{ department }}</span></router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="show-room" v-if="agency && insideMedias.length">
      <div class="grid-container">
        <h2>Le show-room</h2>
        <p>
          Découvrez notre show-room, un espace d'accueil chaleureux où vous pourrez explorer une large sélection d'échantillons d’équipements et de matériaux, pour visualiser concrètement les options qui donneront vie à votre future maison.
        </p>
        <div class="grid-x grid-margin-x align-center grid-margin-y">
          <img v-for="media in insideMedias" class="cell medium-6 small-12" :key="media.agencyMediaId" :src="media.url" />
        </div>
      </div>
    </div>

    <section class="build-zones" v-if="agencyInformations">
      <h2>La zone de construction</h2>
      <template v-if="agencyInformations.content.buildingZone">
        <p class="intro" v-if="agencyInformations.content.buildingZone.content" v-html="richTextToHTML(agencyInformations.content.buildingZone.content)"></p>
      </template>
      <div v-if="agencyInformations.content.buildingZone" class="grid-container">
        <img v-if="mapMedia" :src="mapMedia.url"/>
        <div v-if="agencyInformations.content.buildingCities.content" v-html="richTextToHTML(agencyInformations.content.buildingCities.content)"></div>
      </div>
    </section>

    <section class="reviews" v-if="reviews && infos">
      <div class="grid-container">
        <div class="list" v-if="reviews && infos">
          <div>
            <div class="resume">
              <h2>Les avis clients</h2>
              <review-certification :text="agency.name" :rating="infos.agencyRating" />
              <div class="tags">
                <span>
                  <strong>{{ infos.agencyReviewsCount }} </strong>
                  <small>avis certifiés</small>
                </span>
                <span>
                  <small>{{ agency.name }}</small>
                </span>
                <span>
                  <small>Excellent ⭐️ ⭐️ ⭐️ ⭐️ ⭐️</small>
                </span>
              </div>
              <p>
                Chez Maisons Alysia - {{ agency.name }}, ce sont <strong>nos clients</strong> qui parlent le mieux de leur expérience.
              </p>
              <p>
                Découvrez leurs <strong>avis vérifiés et authentiques</strong> sur la construction de leur maison. Ces retours nous permettent de progresser chaque jour, d’ajuster nos services et de rester à l’écoute de vos besoins.
              </p>
              <p>
                Tous les avis sont vérifiés par un tiers de confiance, <strong>Immodvisor</strong>, pour garantir leur fiabilité. Parcourez leurs témoignages pour vous faire une idée concrète de notre accompagnement.
              </p>
            </div>
            <review-card v-for="review in reviews.slice(0,Math.floor(reviews.length / 2))" :key="review.id" :review="review" :agency="agency"/>
          </div>
          <div>
            <review-card v-for="review in reviews.slice(Math.floor(reviews.length / 2), reviews.length)" :key="review.id" :review="review" :agency="agency"/>
          </div>
        </div>
      </div>
      <router-link v-if="agency" :to="{ name: 'reviews-agency', params: { slugName: agency.slugName }}">
        <app-button>Lire tous les avis</app-button>
      </router-link>
    </section>

    <section class="socials">
      <h2>Les réseaux sociaux</h2>
      <p>Suivez-nous sur les réseaux sociaux pour être informé de nos actualités.</p>
      <div v-if="agency" class="grid-container">
        <div class="facebook-iframe" v-if="agency.facebookId">
          <div class="header">
            <img src="@/assets/img/pages/facebook.png" alt="Facebook" />
          </div>
          <iframe class="facebook" :src="`https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2F${agency.facebookId}&tabs=timeline&width=400&height=800&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false`" width="400" height="800" style="border:none;overflow:hidden" scrolling="no" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>
        </div>

        <div class="instagram-iframe">
          <div class="header">
            <img src="@/assets/img/pages/instagram.png" alt="Instagram" />
          </div>
          <iframe class="desktop instagram" src="https://embedsocial.com/api/pro_hashtag/47bd827857319403faac4e0d167a6ca300dbe4f4" width="800px" height="1200px" frameborder="0" marginheight="0" marginwidth="0" style="border: none; overflow: hidden;"></iframe>
          <iframe class="mobile instagram" src="https://embedsocial.com/api/pro_hashtag/47bd827857319403faac4e0d167a6ca300dbe4f4" width="400px" height="2400px" frameborder="0" marginheight="0" marginwidth="0" style="border: none; overflow: hidden;"></iframe>
        </div>
      </div>
    </section>

    <!-- CONTACT FORM -->
    <form v-if="agency" ref="contactForm" @submit.prevent="contactAgency" id="agency-contact-form">
      <div class="grid-container">
        <h2>Contacter mon agence</h2>
        <p class="intro">
          Pour toute question ou demande d'informations, n'hésitez pas à remplir notre formulaire de contact. Notre équipe s'engage à vous répondre rapidement afin de vous accompagner dans votre projet de construction.
        </p>
        <div class="grid-x grid-margin-x grid-margin-y align-middle">
          <div class="cell medium-6">
            <app-input required v-model="contact.name" label="Mon nom" placeholder=""></app-input>
          </div>
          <div class="cell medium-6">
            <app-input required v-model="contact.firstname" label="Mon prénom" placeholder=""></app-input>
          </div>
          <div class="cell medium-6">
            <app-input required v-model="contact.email" label="Mon email" type="email" placeholder=""></app-input>
          </div>
          <div class="cell medium-6">
            <app-input required v-model="contact.phone" label="Mon numéro de téléphone" type="text" placeholder=""></app-input>
          </div>
          <div class="cell medium-12">
            <app-select required v-model="contact.subject" label="Ma demande" :options="[
              { name: 'Obtenir un rendez-vous', label: 'Obtenir un rendez-vous' },
              { name: 'Être rappelé•e', label: 'Être rappelé•e' },
              { name: 'Plus d’informations', label: 'Plus d’informations' },
            ]" />
          </div>
          <div class="cell medium-12">
            <app-textarea class="cell small-12" required v-model="contact.message" label="Mon message" placeholder=""></app-textarea>
          </div>
          <div class="cell auto">
            <app-checkbox value="true" :input="contact.agreement" :required="true" name="agree">J'accepte d'être recontacté•e *</app-checkbox>
          </div>
          <div class="cell small-12">
            <p class="legals">
              Conformément aux dispositions des articles L. 223-1 et suivants du Code de la consommation, tout consommateur a le droit de s'inscrire sur une liste d'opposition au démarchage téléphonique, dont les coordonnées et modalités de saisine sont disponibles sur le site <a href="https://www.bloctel.gouv.fr" target="_blank">www.bloctel.gouv.fr</a>.
            </p>
          </div>
          <div class="cell small-12">
            <app-button class="cell shrink" type="submit">Envoyer ma demande</app-button>
          </div>
          <div class="cell small-12">
            <p class="legals">
              Pour en savoir plus sur l’usage de vos données recueillies via ce formulaire et exercer vos droits, notamment concernant l’accès et la suppression de vos informations personnelles, nous vous invitons à consulter notre politique de confidentialité.
            </p>
            <p class="legals" v-if="agencyInformations && agencyInformations.content.legalMentions.filename">
              Consulter les <a :href="agencyInformations.content.legalMentions.filename" target="_blank"> mentions légales de l’{{ agency.name }}</a>.
            </p>
          </div>
        </div>
      </div>
    </form>
  </section>
</template>

<script>
import agencyApi from '@/services/api/agency';
import Storyblok from '@/services/storyblok';
import utils from '@/services/utils/utils';
import AgencyJobLabel from '@/services/enums/agency-job-label.enum';
import jobTitles from '@/services/data/agencyGenderJobs.json';

import AgencyMap from '@/components/general/AgencyMap.vue';
import ReviewCard from '@/views/reviews/ReviewCard.vue';
import ReviewCertification from '@/views/reviews/ReviewCertification.vue';

const contactMessages = [
  `
Bonjour,

J’ai un projet de construction et je souhaiterais obtenir un rendez-vous.

Je suis disponible :

[remplissez ici vos plages horaires disponibles].

Cordialement,
  `,
  `
Bonjour,

J’ai un projet de construction et je souhaiterais être rappelé(e).

Je suis disponible :

[remplissez ici vos plages horaires disponibles].

Cordialement,
  `,
  `
Bonjour,

J’ai un projet de construction et je souhaiterais avoir plus d’informations :

[posez vos questions ici].

Cordialement,
  `,
];

export default {
  name: 'page-agence',
  components: {
    AgencyMap,
    ReviewCard,
    ReviewCertification,
  },
  data() {
    return {
      loading: true,
      agency: null,
      agencyInformations: null,
      medias: [],
      members: [],
      postalCode: null,
      contact: {
        name: null,
        firstname: null,
        email: null,
        phone: null,
        subject: 'Obtenir un rendez-vous',
        message: contactMessages[0],
        agreement: null,
      },
      errors: [],
      department: this.getDepartment(),
      mediasLoaded: false,
      AgencyJobLabel,

      showPhone: false,
      infos: null,
      reviews: null,
      jobTitles,
    };
  },
  watch: {
    mediasLoaded() {
      if (this.outsideMedias.length) {
        const heroURL = this.outsideMedias[0].url;
        document.querySelector('.hero-small').style.backgroundImage = `linear-gradient(180deg, rgba(10, 10, 10, 0.6) 0%, rgba(32, 32, 32, 0.2) 100%), url(${heroURL})`;
      } else if (this.mediasLoaded) {
        document.querySelector('.hero-small').classList.add('default-background'); // permet d'afficher l'image par défaut
      }
    },
    contact: {
      handler() {
        if (this.contact.subject === 'Obtenir un rendez-vous') {
          [this.contact.message] = contactMessages;
        } else if (this.contact.subject === 'Être rappelé•e') {
          [, this.contact.message] = contactMessages;
        } else {
          [, , this.contact.message] = contactMessages;
        }
      },
      deep: true,
    },
  },
  computed: {
    outsideMedias() {
      const outsides = this.medias.filter((media) => media.type === 'OUTSIDE');
      return outsides;
    },
    insideMedias() {
      return this.medias.filter((media) => media.type === 'INSIDE');
    },
    mapMedia() {
      const maps = this.medias.filter((media) => media.type === 'MAP');
      return maps[0] || null;
    },
    immodvisor() {
      if (!this.agency?.immodvisorKey) {
        return {
          id: null,
          widgetKey: null,
        };
      }
      const [id, widgetKey] = this.agency?.immodvisorKey?.split(':');
      return {
        id,
        widgetKey,
      };
    },
  },
  async created() {
    this.loading = true;
    try {
      await this.getAgency();
      await this.getReviews();
      await this.getAgencyInformations();
      await Promise.all([this.getMembers(), this.getMedias()]);
    } catch (error) {
      this.errors.push('L\'agence n\'est pas reconnue par nos services');
    }
    this.loading = false;
    this.getDepartment();
  },
  methods: {
    richTextToHTML(data) {
      return utils.jsonToHTML(data);
    },
    getDepartment() {
      const routeSplit = this.$route.path.split('/');
      return routeSplit[routeSplit.length - 2];
    },
    async getAgency() {
      try {
        const response = await agencyApi.getBySlugName(this.$route.params.slugName);
        this.agency = response;
      } catch (error) {
        throw error;
      }
    },
    async getMembers() {
      try {
        const response = await agencyApi.getAgencyMembers(this.agency.agencyId);
        this.members = response;
      } catch (error) {
        throw error;
      }
    },
    async getMedias() {
      try {
        const response = await agencyApi.getAgencyMedias(this.agency.agencyId);
        this.medias = response;
        this.mediasLoaded = true;
      } catch (error) {
        throw error;
      }
    },
    async getAgencyInformations() {
      try {
        const response = await Storyblok.get('cdn/stories/', {
          version: 'published',
          starts_with: 'agencies', // Récupère uniquement les agences
          'filter_query[agencyCode][like]': this.agency.code, // Filtre avec la propriété agencyCode
        });

        if (response.data.stories && response.data.stories[0]) {
          [this.agencyInformations] = response.data.stories;
        }
      } catch (error) {
        throw error;
      }
    },
    async contactAgency() {
      if (!this.$refs.contactForm.checkValidity()) {
        return;
      }
      try {
        await agencyApi.sendContact(this.agency.agencyId, this.contact);

        this.$analyticsModal.show({
          title: 'Merci',
          text: 'Votre demande a bien été envoyée',
          hasConfirm: true,
          hasCancel: true,
          confirmText: 'Fermer',
          cancelText: 'Retour à l\'accueil',
          queryName: 'remerciement-agence',
          onCancel: async () => {
            await this.$router.push({ name: 'home' }).catch(() => true);
          },
        });
      } catch (error) {
        throw error;
      }
    },
    scrollToContactForm() {
      const form = document.getElementById('agency-contact-form');
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    async getReviews() {
      try {
        if (this.agency.immodvisorKey) {
          const response = await agencyApi.getAgencyReviews(this.agency.agencyId);
          this.infos = {
            agencyRating: response.agencyRating,
            agencyReviewsCount: response.agencyReviewsCount,
            brandRating: response.brandRating,
            brandReviewsCount: response.brandReviewsCount,
          };
          this.reviews = response.reviews.slice(0, 5);
        }
      } catch (error) {
        this.$notification.show({ text: 'Une erreur s’est produite.' });
        throw error;
      }
    },
  },
};

</script>

<style lang="sass">
.page-agence
  background: $medium-bg
  overflow-x: hidden
  .hero-small
    @include hero-small
    background-image: linear-gradient(180deg, rgba(10, 10, 10, 0.6) 0%, rgba(32, 32, 32, 0.2) 100%)
    &.default-background
      background-image: linear-gradient(180deg, rgba(10, 10, 10, 0.6) 0%, rgba(32, 32, 32, 0.2) 100%), url(~@/assets/img/pages/agency-hero.png)

  .card-type-img
    margin: 3rem auto
    .content
      svg
        background: $line
        width: 116px
        height: 106px
        padding: 10px 5px
        fill: $off-white
        border-radius: $global-border-radius

      img
        object-fit: cover
        border-radius: $global-border-radius
        &.member-media
          height: 126px
          width: 116px
        &.map-media
          width: 207px
          height: 207px
      @include breakpoint(small down)
        text-align: center
        .grid-x
          img, svg
            margin-bottom: 1rem

  .team
    padding: 100px 0 120px
    background: $medium-bg
    background: linear-gradient($medium-bg, $medium-bg 500px, $primary 500px, $primary 650px, $medium-bg 650px)
    @media (max-width: 768px)
      background: $medium-bg
    > div
      display: flex
      flex-direction: column
      align-items: center
      gap: 40px
      > h2
        margin-bottom: 40px
        font-size: 3rem
        line-height: 1.3
      > p
        text-align: center
        font-size: 110%
        text-wrap: balance
      .members
        display: flex
        justify-content: space-around
        gap: 30px
        margin: 50px 0
        padding: 0 50px
        width: 100%
        @media (max-width: 768px)
          flex-direction: column
          align-items: center
        > div
          display: flex
          flex-direction: column
          gap: 10px
          flex: 1
          align-items: center
          padding: 40px 20px
          max-width: 240px
          background: white
          border: 1px solid #EAEAEA
          border-radius: 20px
          box-shadow: $drop-shadow
          h3
            margin-top: 20px
            text-align: center
          h4
            margin: 5px 0
            color: $primary
            text-align: center
            text-wrap: balance
          p
            text-align: center
            font-size: 0.9rem
            line-height: 140%
          img
            display: block
            max-width: 170px
            border-radius: 50%
            background: #FAFAFA
            box-shadow: $drop-shadow
            border: 1px solid #A4A4A4

  .agency-contact-container
    padding: 100px 0
    background: white
    .grid-container
      h2
        margin-bottom: 50px
        text-align: center
        font-size: 3rem
        line-height: 1.3
      > p
        text-align: center
        font-size: 1.1rem
      > div
        display: flex
        border-radius: $global-border-radius
        margin: 4rem 0
        align-items: stretch
        height: 780px
        box-shadow: $drop-shadow
      .agency-map-container
        width: 60%
        height: 100%
        border-radius: $global-border-radius 0 0 $global-border-radius
      .agency-contact
        width: 40%
        display: flex
        flex-direction: column
        justify-content: space-between
        padding: 3rem
        background: lighten($black, 10%)
        border-radius: 0 $global-border-radius $global-border-radius 0
        @media (max-width: 768px)
          width: 100% !important
        h3
          margin-bottom: 10px
          font-size: 2rem
        p, h3, a
          color: $white
        button
          margin-top: 10px
          display: flex
          align-items: center
        > pre
          font-family: Ubuntu, sans-serif
          color: #DADADA
          font-size: 0.9rem
          line-height: 160%
        img.outside
          margin-top: 20px
          max-height: 250px
          width: 100%
          border-radius: 5px
          object-fit: cover
        p
          margin-top: 1rem
        .phone
          margin-bottom: 1rem
          width: fit-content
        a:not(.phone)
          margin-top: 2rem
          text-align: center
      @include breakpoint(small down)
        flex-direction: column
        height: fit-content
        .agency-map-container, .agency-contact
          width: auto
        .agency-map-container
          min-height: 200px
        .agency-contact
          border-radius: 0 0 $global-border-radius $global-border-radius

  #agency-contact-form
    padding: 8rem 0
    background: linear-gradient(-115deg, lighten($colored-bg, 70%), transparent)
    h2
      text-align: center
      font-size: 3rem
      line-height: 1.3
    p.intro
      margin: 2rem 0 5rem
      text-align: center
      font-size: 1.1rem
    .label
      font-size: 12px
    > div
      max-width: 1000px
    textarea
      min-height: 220px
    .app-button
      margin: 2rem 0 4rem

  .legals
    font-size: 0.8rem
    a
      font-size: 0.8rem

  .department-name
    text-transform: capitalize
    color: currentcolor
  .widget .right
      position: fixed
      top: 50px
      left: 50px
  .show-room
    padding: 100px 0 150px
    background: linear-gradient(45deg, $black, $colored-bg)
    .grid-container
      > h2
        text-align: center
        margin-bottom: 3rem
        font-size: 3rem
        line-height: 1.3
        color: white
      > p
        margin-bottom: 60px
        color: #DADADA
        font-size: 1.1rem
        text-align: center

    img
      border-radius: $global-border-radius
      height: 400px
    @include breakpoint(small down)
      img
        margin-bottom: 1rem

  @media (max-width: 768px)
    .firstWidget
      display: none !important

  .build-zones
    display: flex
    flex-direction: column
    gap: 20px
    align-items: center
    justify-content: center
    padding: 100px 0 120px
    background: white
    > h2
      margin-bottom: 20px
      font-size: 3rem
      line-height: 1.3
      text-align: center
    > p
      text-align: center
      font-size: 1.1rem
      text-wrap: balance
      a
        font-size: 1.05rem
        color: $primary
        font-weight: bold
    .grid-container
      display: flex
      flex-direction: row
      gap: 50px
      align-items: center
      justify-content: flex-start
      width: 100%
      margin-top: 40px
      padding: 0
      background: linear-gradient(-45deg, $medium-bg 60%, lighten($medium-bg, 10%))
      border: 1px solid #EAEAEA
      border-radius: 20px
      box-shadow: $drop-shadow
      overflow: hidden
      @media (max-width: 768px)
        flex-direction: column
      img
        max-width: 500px
        border-right: 1px solid #DADADA
        background: white
      ul
        margin-top: 10px
        padding-left: 16px
      > div
        padding: 20px
        p, li, a
          font-size: 1.1rem
        li
          list-style-type: disc
        li ~ li
          margin-top: 5px

  .socials
    display: flex
    flex-direction: column
    gap: 20px
    align-items: center
    justify-content: center
    padding: 100px 0 80px
    background: $medium-bg
    > h2
      margin-bottom: 20px
      font-size: 3rem
      line-height: 1.3
      text-align: center
    > p
      text-align: center
      font-size: 1.1rem
      text-wrap: balance
    .grid-container
      display: flex
      align-items: flex-start
      justify-content: center
      gap: 20px
      margin-top: 20px
      @media (max-width: 1200px)
        flex-direction: column
    .instagram-iframe, .facebook-iframe
      border-radius: 20px
      box-shadow: $drop-shadow
      background: #FAFAFA
      overflow: hidden
      .header
        display: flex
        align-items: center
        justify-content: center
        gap: 10px
        padding: 20px
        background: #222222
        img
          width: 50px
    .facebook-iframe
      .header
        background: #3B5998
    iframe.facebook
      margin: 0 auto 0
      min-width: 400px
    .instagram.mobile
      display: none
    @media (max-width: 868px)
      .instagram.desktop
        display: none
      .instagram.mobile
        display: block
  .reviews
    display: flex
    flex-direction: column
    justify-content: center
    padding: 100px 0 100px
    background: linear-gradient(-15deg, lighten($primary, 40%), lighten($home-bg, 20%))
    > a
      display: block
      margin: 20px auto 0
      text-decoration: none !important
  .list
    display: flex
    gap: 30px
    padding: 40px
    @media (max-width: 900px)
      flex-direction: column
    > div
      display: flex
      flex: 1
      flex-direction: column
      gap: 30px
      @media (max-width: 768px)
        flex-direction: column
    .resume
      position: relative
      display: flex
      flex-direction: column
      gap: 20px
      padding-bottom: 10px
      h2
        font-size: 3rem
        line-height: 120%
        text-wrap: balance
      .tags
        display: flex
        gap: 10px
        margin: 10px 0 20px
        @media (max-width: 768px)
          flex-direction: column
        > span
          padding: 5px 15px
          border-radius: 20px
          background: $primary
          color: white
          font-size: 0.9rem
          text-align: center
          &:last-child
            background: $colored-bg
          &:first-child
            background: #fd4e2a
</style>
